/* eslint-disable no-nested-ternary */
import { useState, FC, useEffect } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { AnyArgVoidReturnFunction } from 'types/utils';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { segment } from '@actions/analytics';
import useCheckoutEventProps from '@hooks/useCheckoutEventProps';
import { getSubscriptionTypeLabelFromPriceMode } from '@utils/subscriptionHelpers/getSubscriptionTypeForAnalytics';
import { Spacings, mediaQuery } from '@styles/index';
import TTUBanner from '@cont/TalkToUnacademy/Banner';
import useTTUStore from '@stores/ttu';
import { noOp } from '@constants/empty';
import { showTTUOption } from '@api/fetchers/ttu';
import Dynamic from '@base/Dynamic/Dynamic';
import {
  getTTUShow,
  getShowTTUCurtain,
  getTTUIsGoalEnabled
} from '@stores/selectors/ttuSelectors';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsSubscribedToGoal } from '@api/selectors/subscription';
import Typography from 'aquilla/core/atoms/Typography';
import { useIsMobile } from '@stores/AppCommonData';
import { getIsLoggedIn } from '@api/selectors/auth';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { useToggleable } from '@stores/toggleables';
import SubscriptionBenefitsMWeb from '@cont/NewSubscription/NewSubscriptionBenefits/SubscriptionBenefitsMWeb';
import SubscriptionBenefitsWeb from '@cont/NewSubscription/NewSubscriptionBenefits/SubscriptionBenefitsWeb';
import { CHECKOUT_SUBSCRIPTION_TYPE_SELECTED } from '../constants/segment';
import OfflineCentres from './OfflineCentres';
import NewPlanSelection from './MwebPlanSelectionRevamp/PlanSelection';

const TTURenderer = Dynamic(() => import('@layout/TTURenderer'));

const StyTTUBanner = styled(TTUBanner)`
  margin-top: ${Spacings.SPACING_10B} !important;
`;

const TTUWrapper = styled.div`
  grid-column: span 24;
  display: flex;
  ${mediaQuery.uptoMobile} {
    grid-column: span 8;
  }
`;

const OFflineCentreFlatWrapper = styled.div`
  grid-column: span 24;

  ${mediaQuery.uptoMobile} {
    grid-column: span 8;
  }
  background-color: var(--color-i-white);
  padding: ${Spacings.SPACING_6B};
  box-shadow: ${boxShadowGenerator()};
  border-radius: ${Spacings.SPACING_2B};
`;

const BenefitsWrapper = styled.div`
  grid-column: span 24;
  ${mediaQuery.uptoMobile} {
    grid-column: span 8;
  }

  > div {
    width: 100%;
    margin: 0;
  }
`;

const HeaderContainer = styled.div`
  grid-column: span 24;
  padding-bottom: ${Spacings.SPACING_5B};
  ${mediaQuery.uptoMobile} {
    grid-column: span 8;
    padding-bottom: ${Spacings.SPACING_3B};
  }
`;

interface PreCheckoutGoalData {
  uid: string;
  name: string;
}

interface OfflineCentreImage {
  url?: Array<{ url?: string }>;
}

interface PreCheckoutSubscriptionFeatures {
  hasOfflineCentres?: boolean;
  offlineCentreHeading?: string;
  offlineCentreDesc?: string;
  offlineCitiesInfo?: string;
  offlineBatchesInfo?: string;
  offlineCentreImages?: OfflineCentreImage[];
  features?: Array<{ isOffline?: boolean }>;
  offlineCentreVideoURL?: string;
  offlineCentreVideoThumbnail?: Array<{ url?: string }>;
}

interface LowestPriceMap {
  plusLowestPrice?: number;
  iconicLowestPrice?: number;
  iconicProLowestPrice?: number;
  liteLowestPrice?: number;
}

interface PreCheckoutProps {
  showPlans: AnyArgVoidReturnFunction;
  goalData: PreCheckoutGoalData;
  lowestPriceMap: LowestPriceMap;
  subscriptionFeatures?: PreCheckoutSubscriptionFeatures;
}

const PreCheckout: FC<PreCheckoutProps> = ({
  showPlans,
  goalData,
  lowestPriceMap,
  subscriptionFeatures
}) => {
  const router = useRouter();
  const { goalUID, source } = router.query;
  const isMobile = useIsMobile();
  const mwebPlanSelectionV2 = source === 'app' && isMobile;
  const [selectedPriceMode, setSelectedPriceMode] = useState<
    string | undefined
  >();
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const isGoalEnabled = useTTUStore(getTTUIsGoalEnabled);
  const isGoalSubscribed = useMyInfo(getIsSubscribedToGoal(goalUID));
  const ttuShow = useTTUStore(getTTUShow);
  const showTTUCurtain = useTTUStore(getShowTTUCurtain);
  const [backButton, setBackButton] = useState({
    value: false,
    fn: noOp
  });
  const hasOfflineCentres = subscriptionFeatures?.hasOfflineCentres;
  const handleBackPress = () => backButton.fn();
  const ttuOptions = {};

  const eventProps = useCheckoutEventProps(goalData.uid);

  const selectBtnHandler = (priceMode: string) => {
    if (isLoggedIn) {
      segment.track(CHECKOUT_SUBSCRIPTION_TYPE_SELECTED, {
        ...eventProps,
        subscription_type: getSubscriptionTypeLabelFromPriceMode(priceMode)
      });
      showPlans(priceMode.toLowerCase());
    } else {
      setSelectedPriceMode(priceMode);
      openLoginDrawer();
    }
  };

  const handlePlanSelect = (type: string) => {
    if (isLoggedIn) {
      showPlans(type.toLowerCase());
    } else {
      setSelectedPriceMode(type);
      openLoginDrawer();
    }
  };

  useEffect(() => {
    if (isLoggedIn && selectedPriceMode && selectedPriceMode !== 'offline')
      selectBtnHandler(selectedPriceMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const getTTUBanner = () => {
    if (isGoalEnabled && !isGoalSubscribed)
      return <StyTTUBanner goalDetails={goalData} />;
    return null;
  };

  const onCloseTTU = () => {
    setBackButton({ value: false, fn: noOp });
    showTTUCurtain(false);
    showTTUOption(goalUID);
  };

  if (!lowestPriceMap) return null;

  return mwebPlanSelectionV2 ? (
    <NewPlanSelection />
  ) : (
    <>
      <HeaderContainer>
        <Typography as="h1" variant="h3">
          Subscription plans
        </Typography>
      </HeaderContainer>

      <BenefitsWrapper>
        <SubscriptionBenefitsMWeb
          data={lowestPriceMap}
          goalName={goalData.name}
          goal={goalData}
          onSelectPlan={handlePlanSelect}
        />
        <SubscriptionBenefitsWeb
          data={lowestPriceMap}
          goalName={goalData.name}
          goal={goalData}
          onSelectPlan={handlePlanSelect}
        />
      </BenefitsWrapper>

      {hasOfflineCentres && (
        <OFflineCentreFlatWrapper>
          <OfflineCentres
            subscriptionFeatures={subscriptionFeatures}
            setSelectedPriceMode={setSelectedPriceMode}
            selectedPriceMode={selectedPriceMode}
          />
        </OFflineCentreFlatWrapper>
      )}
      <TTUWrapper>{getTTUBanner()}</TTUWrapper>
      <TTURenderer
        // @ts-ignore
        onCloseTTU={onCloseTTU}
        showTTU={ttuShow}
        backButton={backButton}
        setBackButton={setBackButton}
        handleBackPress={handleBackPress}
        ttuTriggerSource="Scroll Trigger"
        ttuOptions={ttuOptions}
      />
    </>
  );
};

export default PreCheckout;
